/* eslint-disable object-property-newline */
import { Alert, Card, Col, Container, Image, Ratio, Row } from "react-bootstrap";
import {
	Marker,
	StaticGoogleMap
} from "react-static-google-map";
import { useCallback, useMemo, useState } from "react";
import { DocumentTextIcon } from "@heroicons/react/outline";
import PropTypes from "prop-types";

import CoursesListTable from "../../../components/CoursesList/CoursesListTable";
import GoogleMapIframe from "./GoogleMapIframe";
import LwcErrorBoundary from "../../../components/LwcErrorBoundary";
import RegistrationClosedMessage from "../../../components/RegistrationClosedMessage";

import { googleMapsLink, isInPersonEvent, registrationIsOpen, urlLocationString } from "../../../helpers/event";
import { googleAPIKey } from "../../../helpers/authHelpers";
import { useCourseRegistration } from "../../../services/CourseRegistrationProvider";


const CourseBody = ({ course, courseList }) => {
	const {
		addCourse,
		removeCourse
	} = useCourseRegistration();
	const handleSeriesAdd = useCallback(({ target }) => {
		if (target.checked) {
			addCourse(course);
		} else {
			removeCourse(course);
		}
	}, [course, addCourse, removeCourse]);

	const [newCoursesToRegister, setNewCoursesToRegister] = useState([]);
	const registrationClosed = useMemo(() => !registrationIsOpen(course), [course]);

	const thisCourseList = useMemo(() => {
		let list = courseList;
		if (course.series) {
			list = list.map((listCourse) => ({ ...listCourse, series: course.series }));
		}
		return list;
	}, [course]);

	return (
		<section className="class-details px-lg-5 pt-5 mb-5">
			<Container fluid>
				<Row className="mb-5">
					<Col lg={8} md={7} sm={12}>
						{registrationClosed ? (
							<Alert className="mb-5">
								<RegistrationClosedMessage course={course} />
							</Alert>) : null}

						{ course.description ? (
							<>
								<h4 className="d-flex align-items-center">
									<DocumentTextIcon className="i text-blue fs-16 pe-2" />

									<span className="fs-16 text-uppercase text-neutral3">
										Description
									</span>
								</h4>

								<div className="text-neutral3" dangerouslySetInnerHTML={{ __html: course.description }} />
							</>) : null}
					</Col>

					<Col className="text-center single-class-image-container" lg={4} md={5} sm={12}>
						{course.image ? (
							<Ratio aspectRatio="1x1" className="mb-4">
								<Image
									className="mb-4"
									fluid
									rounded
									src={course.image.sourceUrl}
								/>
							</Ratio>
						) : null}

						{isInPersonEvent(course) && course.location ? (
							<LwcErrorBoundary>
								<a className="d-block" href={googleMapsLink(course)} rel="noreferrer" target="_blank">
									<StaticGoogleMap apiKey={googleAPIKey()} className="rounded-2" size="400x200" zoom={12}>
										<Marker color="red" location={urlLocationString(course)} />
									</StaticGoogleMap>
								</a>
							</LwcErrorBoundary>) : null}
					</Col>
				</Row>

				<Row className="mb-5 gx-0">
					<Col>
						<Card className="px-2">
							<Card.Body>
								<CoursesListTable
									courses={thisCourseList}
									onChange={handleSeriesAdd}
									selectable={false}
									selected={newCoursesToRegister}
									setNewCoursesToRegister={setNewCoursesToRegister}
									showRemoveButton={courseList.length === 1}
									title={course.series ? "All Classes in Series" : "This Class"}
								/>
							</Card.Body>
						</Card>

						{!!course.recurringClasses?.courses?.length && (
							<Card className="px-2 mt-4">
								<Card.Body>
									<CoursesListTable
										courses={course.recurringClasses.courses}
										selectable
										selected={newCoursesToRegister}
										setNewCoursesToRegister={setNewCoursesToRegister}
										showRemoveButton
										title="This class is also offered the following times:"
									/>
								</Card.Body>
							</Card>
						)}
					</Col>
				</Row>

				{course.type && course.type.indexOf("in-person") !== -1 && course.location ? (
					<Row className="my-5">
						<Col>
							<h3>Location</h3>
							<GoogleMapIframe address={course.location.address} />
						</Col>
					</Row>) : null}
			</Container>
		</section>
	);

};

CourseBody.propTypes = {
	course: PropTypes.shape({
		description: PropTypes.string,
		end: PropTypes.instanceOf(Date),
		facilitator: PropTypes.string,
		id: PropTypes.number,
		image: PropTypes.shape({
			sourceUrl: PropTypes.string
		}),
		location: PropTypes.shape({
			address: PropTypes.shape({})
		}),
		recurringClasses: PropTypes.shape({
			courses: PropTypes.arrayOf(PropTypes.shape())
		}),
		series: PropTypes.string,
		start: PropTypes.instanceOf(Date),
		summary: PropTypes.string,
		title: PropTypes.string,
		type: PropTypes.arrayOf(PropTypes.string)
	}).isRequired,
	courseList: PropTypes.oneOfType([
		PropTypes.bool,
		PropTypes.arrayOf(PropTypes.object)
	])
};

CourseBody.defaultProps = {
	courseList: false
};

export default CourseBody;
